import React, { useCallback, useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import BulkOfferRequestTableSection from "./BulkOfferRequestTableSection";
import {
    fetchBulkOfferRequests,
    deleteBulkOfferRequest
} from "../../../redux/slice/marketplaceSlice";
import ViewBulkOfferRequestModal from "./ViewBulkOfferRequestModal";
import CommonDeletePopup from "../../../components/delete-popup";
import { usePermission } from "../../../hooks/usePermission";
import { TableSearch } from "../../../components/form/Fields";

const MODULE_SLUG = "marketplace_bulk_offer_requests";

export default function BulkOfferRequestList() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>Bulk Offer Requests
            </h4>
        </div>
    );

    const {
        bulkOfferRequests,
        bulkOfferRequestPagination,
        isLoading,
        isInitialLoad
    } = useSelector((state) => state.marketplace);
    const { hasPermission } = usePermission();

    const canDelete = hasPermission(MODULE_SLUG, "delete");

    // Read values from URL search params
    const currentPage = parseInt(searchParams.get("page") || "1");
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

    const limitVal = parseInt(searchParams.get("limit") || "10");
    const limit = useMemo(() => ({ value: limitVal, label: limitVal }), [limitVal]);

    const requestId = searchParams.get("requestId");

    // View Modal state
    const [viewModal, setViewModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);

    // Delete Modal state
    const [deleteModal, setDeleteModal] = useState({ show: false, itemId: null });

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // Sync debouncedSearch to searchParams
    useEffect(() => {
        const currentSearchParam = searchParams.get("search") || "";
        if (debouncedSearch !== currentSearchParam) {
            const currentParams = Object.fromEntries(searchParams.entries());
            if (debouncedSearch) {
                setSearchParams({ ...currentParams, search: debouncedSearch, page: 1 });
            } else {
                delete currentParams.search;
                setSearchParams({ ...currentParams, page: 1 });
            }
        }
    }, [debouncedSearch, searchParams, setSearchParams]);

    // Sync searchTerm state if searchParams changes from outside (e.g. back navigation)
    useEffect(() => {
        const urlSearch = searchParams.get("search") || "";
        setSearchTerm((prev) => (prev !== urlSearch ? urlSearch : prev));
    }, [searchParams]);

    // Sync selected request modal view on mount/change of requestId or bulkOfferRequests list
    useEffect(() => {
        if (requestId && bulkOfferRequests?.length > 0) {
            const req = bulkOfferRequests.find(item => String(item.id) === String(requestId));
            if (req) {
                setSelectedRequest(req);
                setViewModal(true);
            }
        } else {
            setViewModal(false);
            setSelectedRequest(null);
        }
    }, [requestId, bulkOfferRequests]);

    const fetchList = useCallback(async () => {
        const params = {
            page: currentPage,
            limit: limitVal,
            search: debouncedSearch
        };
        dispatch(fetchBulkOfferRequests(params));
    }, [dispatch, currentPage, limitVal, debouncedSearch]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleView = (item) => {
        const currentParams = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...currentParams, requestId: item.id });
    };

    const handleDeleteClick = (item) => {
        setDeleteModal({ show: true, itemId: item.id });
    };

    const confirmDelete = async () => {
        if (deleteModal.itemId) {
            await dispatch(deleteBulkOfferRequest(deleteModal.itemId));
            setDeleteModal({ show: false, itemId: null });
            fetchList();
        }
    };

    const handlePageChange = (newPage) => {
        const currentParams = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...currentParams, page: newPage });
    };

    const handleLimitChange = (newLimitVal) => {
        const currentParams = Object.fromEntries(searchParams.entries());
        setSearchParams({ ...currentParams, limit: newLimitVal, page: 1 });
    };

    const actionList = [
        {
            title: "View",
            handler: (row) => handleView(row)
        },
        {
            title: "Delete",
            handler: (row) => handleDeleteClick(row),
            isVisible: () => canDelete,
            className: "text-danger"
        }
    ];

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="bg-white rounded-2 overflow-hidden common-shadow">
                    <div className="p-4 d-flex align-items-center justify-content-between border-bottom flex-wrap gap-3">

                        <TableSearch value={searchTerm}
                            placeholder="Search requests..."
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }} />
                    </div>

                    <BulkOfferRequestTableSection
                        data={bulkOfferRequests}
                        isLoading={isLoading}
                        isInitialLoad={isInitialLoad}
                        onView={handleView}
                        currentPage={currentPage}
                        setCurrentPage={handlePageChange}
                        pageSize={limit.value}
                        setPageSize={handleLimitChange}
                        totalItems={bulkOfferRequestPagination?.total}
                        actionList={actionList}
                    />
                </div>

                <ViewBulkOfferRequestModal
                    isOpen={viewModal}
                    onClose={() => {
                        const currentParams = Object.fromEntries(searchParams.entries());
                        delete currentParams.requestId;
                        setSearchParams(currentParams);
                    }}
                    title="Bulk Offer Request Details"
                    size="lg"
                    data={selectedRequest}
                />

                <CommonDeletePopup
                    show={deleteModal.show}
                    onHide={() => setDeleteModal({ show: false, itemId: null })}
                    onClickSave={confirmDelete}
                    content="Are you sure you want to delete this bulk offer request? This action cannot be undone."
                    saveTitle="Delete"
                />
            </Container>
        </>
    );
}
